import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { Image } from '@/components/common/image';
import { v4 as uuid } from 'uuid';
import { MaterialFolderItemData } from '@/services/user-material-get-folder-items';
import { Video } from '@/components/common/video';
import { MaterialEmptyImage } from '@/components/agent-dashboard-user-detail/assets';
import { formatAudioTime } from '@/utils/format-audio-time';
import { ViewImageFull } from '@/components/common/view-image-full';
import { PreviewVideoImageFull } from '@/components/common/preview-video-image-full';

// 骨架屏组件
const MaterialModalSkeleton: React.FC = () => {
  const skeletonItems = Array.from({ length: 12 });

  return (
    <div className='grid grid-cols-5 gap-2'>
      {skeletonItems.map((_, index) => (
        <div key={index} className="w-[160px] h-[160px] relative">
          <div
            className='size-full overflow-hidden flex-shrink-0'
          >
            <div className="size-full rounded-[12px] bg-gray-200 animate-pulse"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

interface MaterialModalDetailProps {
  name?: string
  // 素材库目录内文件列表数据
  materialFolderItems?: MaterialFolderItemData[];
  loading?: boolean;
  trigger?: React.ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSave?: (ids: string[]) => void;
}

/** 素材详情弹窗 */
export const MaterialModalDetail: React.FC<MaterialModalDetailProps> = ({
  trigger,
  name,
  materialFolderItems,
  loading,
  open: externalOpen,
  onOpenChange,
  onSave,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);

  const titleRef = useRef<HTMLHeadingElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);


  const [previewList, setPreviewList] = useState<{ url: string, type: "video" | "image" }[]>([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    if (!open) {
      setPreviewList([]);
      setCurrentPreviewIndex(null);
    }
  };

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
    setPreviewList([]);
    setCurrentPreviewIndex(null);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  const handlePreview = (url: string | string[], index?: number) => {
    setPreviewList(materialFolderItems.map(item => ({
      url: item.vectorLibraryFileUrl,
      type: item.itemType as "video" | "image",
    })));
    setCurrentPreviewIndex(index ?? 0);
  }

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[881px] max-h-[738px] min-h-[738px] flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-4 border-b border-[rgba(233,234,235,1)]">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center'
          >
            <span>{name || t("agent_dashboard.user_detail.material.detail_modal.title")}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='h-full flex-1 p-6 select-none overflow-y-auto'>
          {loading ? (
            <MaterialModalSkeleton />
          ) : materialFolderItems && materialFolderItems.length > 0 ? (
            <div className='grid grid-cols-5 gap-2'>
              {materialFolderItems.map((item, index) => (
                <div key={item.id} className="w-[160px] h-[160px] relative">
                  <div
                    className='size-full overflow-hidden flex-shrink-0 cursor-pointer'
                    onClick={() => handlePreview(item.vectorLibraryFileUrl, index)}
                  >
                    {item.itemType === 'image' && (
                      <Image
                        src={item.vectorLibraryFileUrl}
                        className="size-full rounded-[12px] object-contain"
                      />
                    )}
                    {item.itemType === 'video' && (
                      <div className='size-full'>
                        <Video
                          ref={videoRef}
                          src={item.vectorLibraryFileUrl}
                          className="size-full rounded-[12px] object-contain"
                        />
                        <div className='absolute top-1 left-1 text-[rgba(255,255,255,1)] text-[9px] font-semibold border border-[rgba(0,0,0,0.5)] bg-[rgba(0,0,0,0.5)] px-1 py-0.5 rounded-[4.9px]'>{formatAudioTime(item.duration)}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='w-full h-full min-h-[500px] relative flex flex-col justify-center items-center'>
              <MaterialEmptyImage className='w-[159px] h-[136px]' />
              <span className='text-base text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.material.detail_modal.empty_tips")}</span>
            </div>
          )}
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full flex justify-end px-6 py-6 border-t border-[rgba(233,234,235,1)]'>
          <TextButton
            label={t('agent_dashboard.user_detail.material.detail_modal.close')}
            className='w-[147px] text-sm h-10 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-medium !text-[rgba(52,64,84,1)]'
            onClick={handleClose}
          />
        </div>
      </Dialog.Content>
      <PreviewVideoImageFull
        open={previewList.length > 0}
        previewList={previewList}
        currentIndex={currentPreviewIndex}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewList([]);
            setCurrentPreviewIndex(null);
          }
        }}
      />
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}